/* eslint-disable react/jsx-props-no-spreading */
import { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import { Autocomplete, Box, TextField } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { Country, State, City } from "country-state-city";
import useDidUpdateEffect from "../../hooks/useDidUpdateEffect";

const propTypes = {
  location: PropTypes.objectOf(PropTypes.string).isRequired,
  setLocation: PropTypes.func.isRequired,
  clearFields: PropTypes.bool,
  setClearFields: PropTypes.func,
};

const defaultProps = {
  clearFields: false,
  setClearFields: null,
};

const countries = Country.getAllCountries();

const LocationFields = ({
  location,
  setLocation,
  clearFields,
  setClearFields,
}) => {
  const [countryValue, setCountryValue] = useState(
    location.countryCode ? Country.getCountryByCode(location.countryCode) : null
  );
  const [stateValue, setStateValue] = useState(
    location.stateCode
      ? State.getStateByCodeAndCountry(location.stateCode, location.countryCode)
      : null
  );
  const [cityName, setCityName] = useState(location.cityName || null);

  useDidUpdateEffect(() => {
    setStateValue(null);
    setCityName(null);
    setLocation({ ...location, stateCode: "", cityName: "" });
  }, [countryValue]);

  useDidUpdateEffect(() => {
    setCityName(null);
    setLocation({ ...location, cityName: "" });
  }, [stateValue]);

  useEffect(() => {
    if (clearFields) {
      setCountryValue(null);
      setStateValue(null);
      setCityName(null);
      setClearFields(false);
    }
  }, [clearFields]);

  const filterOptions = createFilterOptions({
    matchFrom: "start",
    stringify: (option) => option.name || option,
  });

  return (
    <>
      <Autocomplete
        value={countryValue}
        isOptionEqualToValue={(option, value) => {
          return option.isoCode === value.isoCode;
        }}
        onChange={(e, option) => {
          setCountryValue(option);
          setLocation({
            ...location,
            countryCode: option ? option.isoCode : "",
          });
        }}
        options={countries}
        autoHighlight
        blurOnSelect
        getOptionLabel={(option) => option.name || ""}
        filterOptions={filterOptions}
        renderOption={(props, option) => (
          <Box {...props} key={option.isoCode}>
            {option.flag} {option.name} ({option.isoCode})
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Country"
            inputProps={{
              ...params.inputProps,
              autoComplete: "new-password", // disable autocomplete and autofill
            }}
          />
        )}
      />
      {countryValue ? (
        <Autocomplete
          value={stateValue}
          isOptionEqualToValue={(option, value) => {
            return option.isoCode === value.isoCode;
          }}
          onChange={(e, option) => {
            setStateValue(option);
            setLocation({
              ...location,
              stateCode: option ? option.isoCode : "",
            });
          }}
          options={State.getStatesOfCountry(countryValue.isoCode)}
          autoHighlight
          blurOnSelect
          getOptionLabel={(option) => option.name || ""}
          filterOptions={filterOptions}
          renderOption={(props, option) => (
            <Box {...props} key={option.isoCode}>
              {option.name}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="State"
              inputProps={{
                ...params.inputProps,
                autoComplete: "new-password", // disable autocomplete and autofill
              }}
            />
          )}
        />
      ) : (
        <Autocomplete
          disabled
          value=""
          options={[]}
          renderInput={(params) => <TextField {...params} label="State" />}
        />
      )}
      {countryValue && stateValue ? (
        <Autocomplete
          value={cityName}
          onChange={(e, option) => {
            setCityName(option);
            setLocation({
              ...location,
              cityName: option || "",
            });
          }}
          options={City.getCitiesOfState(
            countryValue.isoCode,
            stateValue.isoCode
          ).map((city) => city.name)}
          autoHighlight
          blurOnSelect
          filterOptions={filterOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              label="City/Area"
              inputProps={{
                ...params.inputProps,
                autoComplete: "new-password", // disable autocomplete and autofill
              }}
            />
          )}
        />
      ) : (
        <Autocomplete
          disabled
          value=""
          options={[]}
          renderInput={(params) => <TextField {...params} label="City/Area" />}
        />
      )}
    </>
  );
};

LocationFields.defaultProps = defaultProps;
LocationFields.propTypes = propTypes;

export default LocationFields;
